import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AdminLoginService } from '../login/adminlogin.service';

export type SessionState = 'active' | 'warning' | 'expired';

export interface SessionDetails {
  state: SessionState;
  remaining: number;
}

interface RefreshResponse {
  token?: {
    rhemaServer?: string;
    refreshToken?: string;
  };
}

const TEN_MINUTES = 10 * 60 * 1000;
export const AUTH_CLIENT_VERSION = '0.1.0';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  readonly sessionDetails$ = new BehaviorSubject<SessionDetails>({
    state: 'active',
    remaining: 0,
  });

  private refreshInFlight?: Observable<string>;
  private timerId?: number;
  private http: HttpClient;

  constructor(
    httpBackend: HttpBackend,
    private router: Router,
    private adminLoginService: AdminLoginService
  ) {
    // Session endpoints bypass the auth interceptor to prevent refresh recursion.
    this.http = new HttpClient(httpBackend);
  }

  startMonitoring(): void {
    if (this.timerId !== undefined) return;
    this.updateSessionDetails();
    this.timerId = window.setInterval(() => this.updateSessionDetails(), 1000);
    window.addEventListener('focus', this.updateSessionDetails);
    window.addEventListener('online', this.updateSessionDetails);
  }

  stopMonitoring(): void {
    if (this.timerId !== undefined) {
      window.clearInterval(this.timerId);
      this.timerId = undefined;
    }
    window.removeEventListener('focus', this.updateSessionDetails);
    window.removeEventListener('online', this.updateSessionDetails);
  }

  saveLoginSession(accessToken: string, refreshToken: string): void {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('sessionExpiresAt', this.getAccessTokenExpiresAt(accessToken));
    localStorage.removeItem('sessionExpired');
    this.updateSessionDetails();
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.markSessionExpired();
      return throwError(() => new Error('Refresh token is unavailable'));
    }

    this.refreshInFlight = this.http
      .post<RefreshResponse>(
        `${environment.services.rhemaServer}/auth/native/refresh`,
        {
          refreshToken,
          deviceId: this.getDeviceId(),
          deviceType: 'web',
          clientVersion: AUTH_CLIENT_VERSION,
        },
        { headers: this.getClientHeaders() }
      )
      .pipe(
        map((response) => {
          const accessToken = response.token?.rhemaServer;
          const replacementRefreshToken = response.token?.refreshToken;
          if (!accessToken || !replacementRefreshToken) {
            throw new Error('Refresh response did not contain replacement tokens');
          }
          this.saveLoginSession(accessToken, replacementRefreshToken);
          return accessToken;
        }),
        catchError((error) => {
          this.markSessionExpired();
          return throwError(() => error);
        }),
        finalize(() => (this.refreshInFlight = undefined)),
        shareReplay(1)
      );

    return this.refreshInFlight;
  }

  logout(): Observable<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    const revokeRequest: Observable<unknown> = refreshToken && navigator.onLine
      ? this.http.post(
          `${environment.services.rhemaServer}/auth/native/logout`,
          { refreshToken },
          { headers: this.getClientHeaders() }
        )
      : of(null);

    // Local logout and navigation must not wait for a potentially slow revoke call.
    this.clearLocalSession();
    return revokeRequest.pipe(
      catchError(() => of(null)),
      map((): void => undefined)
    );
  }

  logoutAndRedirect(): void {
    this.logout().subscribe();
    this.router.navigateByUrl('/login');
  }

  markSessionExpired(): void {
    if (!this.hasAuthenticatedSession()) return;
    localStorage.setItem('sessionExpired', 'true');
    localStorage.removeItem('token');
    this.updateSessionDetails();
  }

  getDeviceId(): string {
    let deviceId = localStorage.getItem('rhemaWebDeviceId');
    if (!deviceId) {
      deviceId = window.crypto?.randomUUID?.() ||
        `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem('rhemaWebDeviceId', deviceId);
    }
    return deviceId;
  }

  getSessionDetails(): SessionDetails {
    if (!this.hasAuthenticatedSession()) return { state: 'active', remaining: 0 };
    if (localStorage.getItem('sessionExpired') === 'true') {
      return { state: 'expired', remaining: 0 };
    }

    try {
      const expiresAt = Date.parse(this.getAccessTokenExpiresAt(localStorage.getItem('token') || ''));
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) return { state: 'expired', remaining: 0 };
      if (remaining <= TEN_MINUTES) return { state: 'warning', remaining };
      return { state: 'active', remaining };
    } catch (_error) {
      return { state: 'expired', remaining: 0 };
    }
  }

  private hasAuthenticatedSession(): boolean {
    // Older logins may not have a refresh token, but still need expiry alerts.
    return localStorage.getItem('isUserLoggedIn') === 'true';
  }

  private getClientHeaders(): HttpHeaders {
    return new HttpHeaders({
      'X-Rhema-Client': 'web',
      'X-Rhema-Client-Version': AUTH_CLIENT_VERSION,
      'X-Rhema-Device-Id': this.getDeviceId(),
      'X-Rhema-Auth-Version': '2',
    });
  }

  private getAccessTokenExpiresAt(accessToken: string): string {
    const payloadPart = accessToken.split('.')[1];
    if (!payloadPart) throw new Error('Access token is invalid');
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(normalized)) as { exp?: number };
    if (!payload.exp || !Number.isFinite(Number(payload.exp))) {
      throw new Error('Access token does not contain a valid expiry');
    }
    return new Date(Number(payload.exp) * 1000).toISOString();
  }

  private updateSessionDetails = (): void => {
    const details = this.getSessionDetails();
    if (details.state === 'expired' && this.hasAuthenticatedSession()) {
      localStorage.setItem('sessionExpired', 'true');
    }
    this.sessionDetails$.next(details);
  };

  private clearLocalSession(): void {
    const deviceId = localStorage.getItem('rhemaWebDeviceId');
    localStorage.clear();
    if (deviceId) localStorage.setItem('rhemaWebDeviceId', deviceId);
    localStorage.setItem('isUserLoggedIn', 'false');
    this.adminLoginService.isLoggedIn = { loggedIn: false, role: '' };
    this.adminLoginService.emitToSubscribers();
    this.sessionDetails$.next({ state: 'active', remaining: 0 });
  }
}
